import { useLocale } from '../i18n/LocaleProvider'
import { useEffect } from 'react'
import { Link } from '../i18n/LocalizedLink'
import { ArrowLeft, ArrowUpRight } from '@phosphor-icons/react'
import Navbar from '../components/Navbar'
import { SiteButtonLink } from '../components/SiteButton'
import { usePageTitle } from '../hooks/usePageTitle'
import './NotFoundPage.css'

interface NotFoundPageProps {
  section?: 'blog' | 'newsroom'
}

export default function NotFoundPage({ section }: NotFoundPageProps) {
  const { t } = useLocale()
  usePageTitle(t("Page not found"))

  useEffect(() => {
    document.body.classList.add('body-notfound')
    const existing = document.querySelector<HTMLMetaElement>('meta[name="robots"]')
    const robots = existing ?? document.createElement('meta')
    const previous = robots.getAttribute('content')
    robots.name = 'robots'
    robots.content = 'noindex, follow'
    if (!existing) document.head.appendChild(robots)

    return () => {
      document.body.classList.remove('body-notfound')
      if (!existing || previous?.includes('noindex')) robots.remove()
      else if (previous === null) robots.removeAttribute('content')
      else robots.content = previous
    }
  }, [])

  const sectionName = section === 'blog' ? t("Blog") : t("Newsroom")

  return (
    <>
      <Navbar lightMode />
      <div className="notfound-page">
        <main className="notfound-main" id="main-content">
          <div className="notfound-copy">
            <h1>{t("Page not found.")}</h1>
            <p className="notfound-description">
              {t('The page you’re looking for may have moved or no longer exists. Let’s get you back.')}
            </p>
            <nav className="notfound-actions" aria-label={t("Page recovery")}>
              <SiteButtonLink to="/" variant="accent" size="compact">
                <ArrowLeft size={17} weight="bold" aria-hidden="true" /> {t("Back to home")} </SiteButtonLink>
              <SiteButtonLink
                to={section ? `/${section}` : '/about'}
                variant="dark"
                size="compact"
              >
                {section ? t('Browse {section}', { section: t(sectionName) }) : t("About Bybeit")}
                <ArrowUpRight size={17} weight="bold" aria-hidden="true" />
              </SiteButtonLink>
            </nav>
          </div>
        </main>

        <footer className="notfound-footer">
          <p>© {new Date().getFullYear()} {t("Bybeit, Inc.")}</p>
          <nav aria-label={t("Explore Bybeit")}>
            <Link to="/company">{t("Company")}</Link>
            <Link to="/blog">{t("Blog")}</Link>
            <Link to="/newsroom">{t("Newsroom")}</Link>
          </nav>
        </footer>
      </div>
    </>
  )
}
